import { Helmet } from "react-helmet-async";

const BUSINESS_DATA = {
  name: "TJP Carpentry & Construction",
  phone: "+447780996775",
  phoneDisplay: "07780 996775",
  url: "https://tjpconstruction.co.uk",
  logo: "https://customer-assets.emergentagent.com/job_b99f5c04-b5b6-42f3-ace7-113e95402a7c/artifacts/d0rzk6rg_17997253177043219.jpg",
  description: "Professional carpentry and construction services in Surrey and SW London. Specializing in loft conversions, house extensions, kitchen fitting, bathroom renovations, fitted wardrobes, and full home refurbishments.",
  areaServed: [
    "Surrey",
    "SW London",
    "Guildford",
    "Woking",
    "Kingston",
    "Richmond",
    "Wimbledon",
    "Epsom",
    "Leatherhead",
    "Cobham"
  ],
  services: [
    {
      name: "Loft Conversions",
      description: "Transform your unused loft into a stunning living space, bedroom, or home office with expert craftsmanship."
    },
    {
      name: "House Extensions",
      description: "Expand your home with expertly crafted side, rear, or garage extensions to create more living space."
    },
    {
      name: "Kitchen Fitting",
      description: "Complete kitchen fitting services from custom cabinetry to worktops, creating your dream kitchen."
    },
    {
      name: "Bathroom Renovations",
      description: "Full bathroom renovation services including tiling, plumbing, and custom fixtures."
    },
    {
      name: "Fitted Wardrobes",
      description: "Bespoke fitted wardrobes designed and built to maximize your storage space."
    },
    {
      name: "Home Refurbishments",
      description: "Complete home refurbishment services to transform and modernize your property."
    }
  ],
  openingHours: [
    { days: "Monday-Friday", hours: "08:00-18:00" },
    { days: "Saturday", hours: "09:00-14:00" }
  ],
  priceRange: "££"
};

// Local Business Schema
export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "@id": `${BUSINESS_DATA.url}/#organization`,
    name: BUSINESS_DATA.name,
    alternateName: "TJP Carpentry",
    url: BUSINESS_DATA.url,
    logo: BUSINESS_DATA.logo,
    image: BUSINESS_DATA.logo,
    description: BUSINESS_DATA.description,
    telephone: BUSINESS_DATA.phone,
    priceRange: BUSINESS_DATA.priceRange,
    currenciesAccepted: "GBP",
    paymentAccepted: "Cash, Bank Transfer",
    areaServed: BUSINESS_DATA.areaServed.map(area => ({
      "@type": "City",
      name: area,
      "@id": `https://en.wikipedia.org/wiki/${area.replace(" ", "_")}`
    })),
    address: {
      "@type": "PostalAddress",
      addressRegion: "Surrey",
      addressCountry: "GB"
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "51.2362",
      longitude: "-0.5704"
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Saturday",
        opens: "09:00",
        closes: "14:00"
      }
    ],
    sameAs: [
      "https://www.instagram.com/tjp_carpentry_and_construction/"
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Carpentry & Construction Services",
      itemListElement: BUSINESS_DATA.services.map((service, index) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: service.name,
          description: service.description
        }
      }))
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Service Schema for individual service pages
export const ServiceSchema = ({ serviceName, serviceDescription, image }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: serviceName,
    description: serviceDescription,
    provider: {
      "@type": "HomeAndConstructionBusiness",
      name: BUSINESS_DATA.name,
      telephone: BUSINESS_DATA.phone,
      url: BUSINESS_DATA.url
    },
    areaServed: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: "51.2362",
        longitude: "-0.5704"
      },
      geoRadius: "30 mi"
    },
    image: image
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// BreadcrumbList Schema
export const BreadcrumbSchema = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${BUSINESS_DATA.url}${item.url}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// FAQ Schema for common questions
export const FAQSchema = ({ faqs }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// WebPage Schema
export const WebPageSchema = ({ title, description, url }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description: description,
    url: `${BUSINESS_DATA.url}${url}`,
    isPartOf: {
      "@type": "WebSite",
      name: BUSINESS_DATA.name,
      url: BUSINESS_DATA.url
    },
    about: {
      "@type": "HomeAndConstructionBusiness",
      name: BUSINESS_DATA.name
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default LocalBusinessSchema;
